import { classifyPaymentError, paymentErrorCodeForTelemetry } from '@/lib/payment-errors';
import { reportAppError, type AppFlow } from '@/lib/telemetry/faro';

/**
 * Classify a caught payment error and report it ONLY if it is ours.
 * Safe to call in every payment catch block AND on every `status: false` response
 * branch — a declined card or a backend-owned code is dropped here.
 *
 * Same total-failure discipline as the auth reporter: this is called from UI code, so
 * nothing it does may propagate.
 *
 * Lives outside faro.ts deliberately — see the plan's Global Constraint 2b.
 */
export function reportPaymentError(flow: AppFlow, err: unknown): void {
  try {
    const kind = classifyPaymentError(err);
    if (kind === 'expected') return;
    reportAppError({
      evt: 'payment_error',
      flow,
      kind,
      code: paymentErrorCodeForTelemetry(err),
      // NOTE: `error` is currently inert. reportAppError only pushes a stack for
      // kind === 'config_regression', and payments deliberately never returns that kind
      // (it would collide with FirebaseAuthConfigRegression's paging rule). Kept in the
      // call so stacks start flowing the moment that changes.
      error: err,
    });
  } catch {
    // Never propagate — telemetry must not break the payment UI.
  }
}
